import moment from 'moment'
import { values, find, propEq } from 'ramda'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import { CollapsibleBox } from '@/components/collapsible'
import CurrencyFormatter from '@/components/currency-formatter'
import Icon from '@/components/icon'
import ResolvePaymentButton from '@/features/Payments/resolve-payment-button'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, colors, fontWeights } from '@/styles'
import AreaTitle from '@/components/area-title'
import { StyledPanel } from '../elements'
import Context from '../context'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const ContentWrapper = styled.div`
  max-width: 700px;
  width: calc(100% - 48px);
  margin: 0 24px 100px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: calc(100% - 32px);
    margin: 0 16px 70px;
  }
`

const StatusPanel = styled(StyledPanel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 30px 80px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 60px 20px 80px;
  }
`

const Title = styled.h2`
  font-size: 26px;
  font-weight: bold;
  letter-spacing: -0.2px;
  line-height: 36px;
  text-align: center;
  margin: 20px 0;
`

const Description = styled.p`
  color: ${colors.mediumGrey};
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  line-height: 19px;
  text-align: center;
  max-width: 500px;
  margin: 0;
`

const DescriptionCompleted = styled(Description)`
  max-width: 400px;
  margin: 0 0 40px;
`

const ValueWrapper = styled.div`
  display: flex;

  ${props =>
    props.totalRow &&
    `
    flex-direction: row;
    padding-top: 30px;
    border-top: 1px solid ${colors.grey};
    font-weight: ${fontWeights.medium};
  `}

  ${props =>
    props.warning &&
    `
    color: ${colors.primary};
  `}
`

const Label = styled.span`
  flex: 9999;
  margin-right: 30px;
`

const Value = styled.span`
  justify-content: flex-end;
  font-weight: ${fontWeights.medium};
`

const LabeledValue = ({ label, value, totalRow, warning }) => (
  <ValueWrapper totalRow={totalRow} warning={warning}>
    <Label>{label}</Label>
    <Value>{value}</Value>
  </ValueWrapper>
)

const InfoTextWrapper = styled.div`
  border-radius: 10px;
  background-color: #e9f2fc;
  padding: 20px 35px;
  margin: 35px 0 0;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 20px 25px;
  }
`

const InfoText = styled.p`
  font-size: 12px;
  letter-spacing: -0.2px;
  line-height: 17px;
  text-align: center;
  margin: 0;
`

const PaymentStatus = ({ handleBack }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { paymentData } = useContext(Context)
  const [currentStep, setCurrentStep] = useState('pending')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  const showPending = currentStep === 'pending'
  const showIncomplete = currentStep === 'incomplete'

  const unresolvedTransaction =
    find(propEq('coinbase_invoice_last_event', 'unresolved'))(
      values(paymentData.payment_transactions)
    ) || {}

  useEffect(() => {
    switch (paymentData.payment_status) {
      case 'collecting_funds':
        return setCurrentStep('pending')
      case 'incomplete':
        return setCurrentStep('incomplete')
      default:
        return setCurrentStep(null)
    }
  }, [paymentData])

  return (
    <>
      <Wrapper>
        <ContentWrapper>
          <AreaTitle>{t('b.payment.view.payment_status.title', 'Payment Status')}</AreaTitle>
          {showPending && (
            <StatusPanel>
              <Icon id="info_circle" width={75} height={75} />
              <Title>{t('b.payment.view.payment_pending_title.text', 'Payment Pending')}</Title>
              <Description>
                {t(
                  'b.payment.view.payment_pending_description.text',
                  'Payment not processed yet. If you’ve sent the payment from your Bitcoin wallet, then no need to worry! It may take up to 6 hours to process. We’ll email you a confirmation once payment is received.'
                )}
              </Description>
            </StatusPanel>
          )}
          {showIncomplete && (
            <>
              <StatusPanel>
                <Icon id="warning" width={75} height={75} />
                <Title>
                  {t('b.payment.view.payment_incomplete_title.text', 'Payment Incomplete')}
                </Title>
                <DescriptionCompleted>
                  {t(
                    'b.payment.view.payment_incomplete_description.text',
                    'Your payment was insufficient due to exchange rate adjustment. To avoid this from happening again, try to complete the transaction right after the payment amount has been exchanged into Bitcoin.'
                  )}
                </DescriptionCompleted>
                <CollapsibleBox
                  title={t('r.payment.view.payment_summary.subtitle', 'Payment Summary')}
                  wrapChildren
                  minWidth={displayMobile ? undefined : 400}
                >
                  <LabeledValue
                    key="confirmation-number"
                    label={t('b.contract.view.confirmation_number.label', 'Confirmation No.')}
                    value={unresolvedTransaction.coinbase_code}
                  />
                  <LabeledValue
                    key="payment-total"
                    label={t('r.payment.view.total.label', 'Total')}
                    value={<CurrencyFormatter showCents value={paymentData.charged} />}
                  />
                  <LabeledValue
                    key="payment-received"
                    label={t('r.payment.view.payment_received.label', 'Payment received')}
                    value={
                      <CurrencyFormatter showCents value={unresolvedTransaction.amount_paid} />
                    }
                  />
                  <LabeledValue
                    key="payment-date"
                    label={t('r.payment.view.payment_date.label', 'Payment date')}
                    value={moment(unresolvedTransaction.paid_at).format('YYYY-MM-DD')}
                  />
                  <LabeledValue
                    totalRow
                    warning
                    key="balance-due"
                    label={t('r.payment.view.balance_due.label', 'Balance due')}
                    value={
                      <CurrencyFormatter
                        showCents
                        value={paymentData.charged - unresolvedTransaction.amount_paid}
                      />
                    }
                  />
                </CollapsibleBox>
              </StatusPanel>
              <InfoTextWrapper>
                <InfoText>
                  {t(
                    'r.payment.edit.payment_incomplete_resolve_info.text',
                    'To pay the remaining balance due to insufficient funds, click “Resolve” to complete your payment. A new window will open to redirect you to our payment service provider. '
                  )}
                </InfoText>
              </InfoTextWrapper>
            </>
          )}
        </ContentWrapper>
      </Wrapper>
      <DefaultButtonBar handleBack={handleBack}>
        {showIncomplete ? (
          <ResolvePaymentButton size="small" paymentData={paymentData} />
        ) : (
          <Button
            theme="primary"
            size="small"
            onClick={() => history.push(`/rental/${paymentData.listing_id}/payments`)}
          >
            {t('common.done', 'Done')}
          </Button>
        )}
      </DefaultButtonBar>
    </>
  )
}

export default PaymentStatus
